/* Albert Tusk — "Our Products" image dropdown (desktop) + mobile drawer list. Vanilla JS, no deps. */
(() => {
  'use strict';
  if (window.__atDropdown) return;
  window.__atDropdown = true;

  const DESKTOP = window.matchMedia('(min-width: 1024px)');
  const dropdowns = new Set();
  const bound = new WeakSet();

  /* ------------------------------------------------------------ Desktop */
  class Dropdown {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-at-dd-toggle]');
      this.panel = root.querySelector('[data-at-dd-panel]');
      this.isOpen = false;
      this.t = 0;
      if (!this.toggle || !this.panel) return;

      if (root.dataset.atDdOpen !== 'click') {           // Customize → "Open dropdown on"
        root.addEventListener('pointerenter', (e) => {
          if (e.pointerType === 'mouse' && DESKTOP.matches) { clearTimeout(this.t); this.t = setTimeout(() => this.open(), 60); }
        });
        root.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'mouse') { clearTimeout(this.t); this.t = setTimeout(() => this.close(), 180); }
        });
      } else {
        root.querySelector('[data-at-dd-link]')?.addEventListener('click', (e) => {
          if (!DESKTOP.matches) return;
          e.preventDefault();
          this.isOpen ? this.close() : this.open();
        });
      }

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      root.addEventListener('keydown', (e) => {
        const items = [...this.panel.querySelectorAll('a')];
        const i = items.indexOf(document.activeElement);
        if (e.key === 'Escape' && this.isOpen) { e.stopPropagation(); this.close(); this.toggle.focus(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); this.open(); items[Math.min(i + 1, items.length - 1)]?.focus(); }
        else if (e.key === 'ArrowUp' && i > -1) { e.preventDefault(); (i === 0 ? this.toggle : items[i - 1]).focus(); }
      });
      root.addEventListener('focusout', (e) => {
        if (this.isOpen && e.relatedTarget && !root.contains(e.relatedTarget)) this.close();
      });
      DESKTOP.addEventListener('change', () => { if (!DESKTOP.matches) this.close(); });
    }
    open() {
      clearTimeout(this.t);
      if (this.isOpen || !DESKTOP.matches) return;
      dropdowns.forEach((d) => d !== this && d.close());
      try { window.Alpine?.store?.('xHeaderMenu')?.closeMenu?.(); } catch (_) { /* no theme store */ }
      this.panel.inert = false;
      this.root.setAttribute('data-open', '');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }
    close() {
      clearTimeout(this.t);
      if (!this.isOpen) return;
      this.root.removeAttribute('data-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.panel.inert = true;
      this.isOpen = false;
    }
  }

  document.addEventListener('pointerdown', (e) => {
    dropdowns.forEach((d) => { if (d.isOpen && !d.root.contains(e.target)) d.close(); });
  });

  /* ------------------------------------------------------------- Mobile */
  // Mystique builds its drawer at runtime, so swap its "Our Products" item for our list.
  const SKIP = '[data-at-dd], [data-at-mob], template, footer, .header__inline-menu, [id*="footer"], .shopify-section-group-footer-group';

  function findDrawerItem(label) {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (n.nodeValue.trim().toLowerCase() === label ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
    });
    let n;
    while ((n = walker.nextNode())) {
      const el = n.parentElement;
      if (!el || el.closest(SKIP)) continue;
      const li = el.closest('li');
      if (!li || li.closest(SKIP) || li.dataset.atMobReplaced) continue;
      if (li.querySelectorAll('a').length < 2) continue; // must be the item with a submenu
      return { li, trigger: el };
    }
    return null;
  }

  // Align with Mystique's items: measure where a sibling item's text starts.
  function syncPadding(node) {
    const measure = () => {
      if (!node.isConnected || !node.offsetWidth) return false;
      const sib = [node.previousElementSibling, node.nextElementSibling].find((el) => el && el.offsetWidth);
      if (!sib) return true;
      const w = document.createTreeWalker(sib, NodeFilter.SHOW_TEXT, {
        acceptNode: (t) => (t.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
      });
      const t = w.nextNode();
      if (!t) return true;
      const r = document.createRange();
      r.selectNodeContents(t);
      const pad = Math.round(r.getBoundingClientRect().left - node.getBoundingClientRect().left);
      if (pad >= 0 && pad < 80) node.style.setProperty('--at-mob-pad-x', `${pad}px`);
      return true;
    };
    if (measure()) return;
    const ro = new ResizeObserver(() => { if (measure()) ro.disconnect(); });
    ro.observe(node);
  }

  function injectMobile() {
    const tpl = document.querySelector('template[data-at-mob-tpl]');
    if (!tpl) return;
    const label = (tpl.dataset.trigger || 'Our Products').trim().toLowerCase();
    const hit = findDrawerItem(label);
    if (!hit) return;
    const node = tpl.content.firstElementChild?.cloneNode(true);
    if (!node) return;
    const cs = getComputedStyle(hit.trigger);
    node.style.setProperty('font-family', cs.fontFamily);
    node.querySelector('.at-mobdd__btn')?.style.setProperty('font-size', cs.fontSize);
    node.dataset.atMobReplaced = '1';
    hit.li.replaceWith(node);
    syncPadding(node);
  }

  let scanQueued = false;
  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    setTimeout(() => { scanQueued = false; injectMobile(); }, 120);
  };
  new MutationObserver((muts) => {
    if (muts.some((m) => m.addedNodes.length)) queueScan();
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Expand / collapse
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-at-mob] [data-at-acc]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation(); // keep Mystique's drawer handlers from treating it as a nav click
    const li = btn.closest('[data-at-mob]');
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    li.classList.toggle('is-open', open);
    if (panel) panel.inert = !open;
  }, true);

  /* --------------------------------------------------------------- Init */
  const init = (scope = document) => {
    scope.querySelectorAll('[data-at-dd]').forEach((el) => {
      if (bound.has(el)) return;
      bound.add(el);
      dropdowns.add(new Dropdown(el));
    });
    injectMobile();
  };
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();